import { Calendar, Clock, MapPin, DollarSign, Shield, User, FileText } from 'lucide-react';
import { BookingData } from './BookingFlow';
import { usePets } from '../../context/PetsContext';

interface BookingSummaryProps {
  bookingData: BookingData;
  onComplete: () => void;
}

export function BookingSummary({ bookingData, onComplete }: BookingSummaryProps) {
  const { pets } = usePets();
  const selectedPetsData = pets.filter(pet => bookingData.selectedPets.includes(pet.id));
  
  // Mock pricing
  const basePrice = 75;
  const petCount = selectedPetsData.length;
  const multiPetDiscount = petCount > 1 ? 10 : 0;
  const subtotal = basePrice * petCount - multiPetDiscount;
  const tax = subtotal * 0.08;
  const total = subtotal + tax;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-xl mb-2 dark:text-white">Review & Book Appointment</h3>
        <p className="text-gray-600 dark:text-white/70">Please review all details before confirming</p>
      </div>

      {/* Pet(s) Summary */}
      <div className="bg-white dark:bg-[#16151D] border border-gray-200 dark:border-white/8 rounded-lg p-6">
        <div className="flex items-center gap-2 mb-4">
          <User className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          <h4 className="dark:text-white">Pet(s)</h4>
        </div>
        <div className="space-y-2">
          {selectedPetsData.map(pet => (
            <div key={pet.id} className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-[#0F0E16] rounded-lg">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-purple-100 dark:from-blue-900/40 dark:to-purple-900/40 rounded-full flex items-center justify-center text-2xl">
                {pet.species === 'dog' ? '🐕' : '🐈'}
              </div>
              <div>
                <div className="dark:text-white">{pet.name}</div>
                <div className="text-sm text-gray-600 dark:text-white/60">{pet.breed}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Professional & Service */}
      <div className="bg-white dark:bg-[#16151D] border border-gray-200 dark:border-white/8 rounded-lg p-6">
        <div className="flex items-center gap-2 mb-4">
          <MapPin className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          <h4 className="dark:text-white">Professional & Service</h4>
        </div>
        <div className="space-y-3">
          <div>
            <div className="text-sm text-gray-600 dark:text-white/60">Business</div>
            <div className="dark:text-white">{bookingData.professional?.name}</div>
          </div>
          <div>
            <div className="text-sm text-gray-600 dark:text-white/60">Service Type</div>
            <div className="capitalize dark:text-white">{bookingData.category}</div>
          </div>
          {bookingData.serviceDetails?.reason && (
            <div>
              <div className="text-sm text-gray-600 dark:text-white/60">Reason</div>
              <div className="capitalize dark:text-white">{bookingData.serviceDetails.reason}</div>
            </div>
          )}
          {bookingData.serviceDetails?.serviceType && (
            <div>
              <div className="text-sm text-gray-600 dark:text-white/60">Service</div>
              <div className="dark:text-white">{bookingData.serviceDetails.serviceType}</div>
            </div>
          )}
        </div>
      </div>

      {/* Date & Time */}
      <div className="bg-white dark:bg-[#16151D] border border-gray-200 dark:border-white/8 rounded-lg p-6">
        <div className="flex items-center gap-2 mb-4">
          <Calendar className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          <h4 className="dark:text-white">Date & Time</h4>
        </div>
        <div className="space-y-2">
          <div className="flex items-center gap-2 dark:text-white">
            <Calendar className="w-4 h-4 text-gray-500 dark:text-white/50" />
            <span>
              {bookingData.appointmentTime?.date.toLocaleDateString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </span>
          </div>
          <div className="flex items-center gap-2 dark:text-white">
            <Clock className="w-4 h-4 text-gray-500 dark:text-white/50" />
            <span>{bookingData.appointmentTime?.time}</span>
          </div>
        </div>
      </div>

      {/* Data Sharing Summary */}
      <div className="bg-white dark:bg-[#16151D] border border-gray-200 dark:border-white/8 rounded-lg p-6">
        <div className="flex items-center gap-2 mb-4">
          <Shield className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          <h4 className="dark:text-white">Data Sharing</h4>
        </div>
        <div className="space-y-2">
          <div>
            <div className="text-sm text-gray-600 dark:text-white/60 mb-2">Shared Information:</div>
            <div className="flex flex-wrap gap-2">
              {bookingData.sharedData.map((data, index) => (
                <span key={index} className="px-3 py-1 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded-full text-sm capitalize">
                  {data.replace('-', ' ')}
                </span>
              ))}
            </div>
          </div>
          <div>
            <div className="text-sm text-gray-600 dark:text-white/60">Access Duration:</div>
            <div className="capitalize dark:text-white">{bookingData.accessDuration.replace('-', ' ')}</div>
          </div>
        </div>
      </div>

      {/* Price Breakdown */}
      <div className="bg-white dark:bg-[#16151D] border border-gray-200 dark:border-white/8 rounded-lg p-6">
        <div className="flex items-center gap-2 mb-4">
          <DollarSign className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          <h4 className="dark:text-white">Price Breakdown</h4>
        </div>
        <div className="space-y-3">
          <div className="flex justify-between dark:text-white">
            <span>Base Service ({petCount} pet{petCount > 1 ? 's' : ''})</span>
            <span>${(basePrice * petCount).toFixed(2)}</span>
          </div>
          {multiPetDiscount > 0 && (
            <div className="flex justify-between text-green-600 dark:text-green-400">
              <span>Multi-pet Discount</span>
              <span>-${multiPetDiscount.toFixed(2)}</span>
            </div>
          )}
          <div className="flex justify-between text-sm text-gray-600 dark:text-white/60">
            <span>Tax (8%)</span>
            <span>${tax.toFixed(2)}</span>
          </div>
          <div className="border-t border-gray-200 dark:border-white/8 pt-3 flex justify-between text-lg dark:text-white">
            <strong>Total</strong>
            <strong>${total.toFixed(2)}</strong>
          </div>
        </div>
      </div>

      {/* Payment Options */}
      <div className="bg-white dark:bg-[#16151D] border border-gray-200 dark:border-white/8 rounded-lg p-6">
        <h4 className="mb-4 dark:text-white">Payment Method</h4>
        <div className="space-y-2">
          <label className="flex items-center gap-3 p-4 border-2 border-blue-600 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-500 rounded-lg cursor-pointer">
            <input
              type="radio"
              name="payment"
              value="now"
              defaultChecked
              className="w-4 h-4 text-blue-600 dark:text-blue-400"
            />
            <div className="flex-1">
              <div className="dark:text-white">Pay Now</div>
              <div className="text-sm text-gray-600 dark:text-white/60">Secure payment to confirm booking</div>
            </div>
          </label>
          <label className="flex items-center gap-3 p-4 border-2 border-gray-300 dark:border-white/20 rounded-lg cursor-pointer hover:border-gray-400 dark:hover:border-white/30">
            <input
              type="radio"
              name="payment"
              value="later"
              className="w-4 h-4 text-blue-600 dark:text-blue-400"
            />
            <div className="flex-1">
              <div className="dark:text-white">Pay at Appointment</div>
              <div className="text-sm text-gray-600 dark:text-white/60">Confirm now, pay on arrival</div>
            </div>
          </label>
        </div>
      </div>

      {/* Terms */}
      <div className="bg-gray-50 dark:bg-[#16151D] border border-gray-200 dark:border-white/8 rounded-lg p-4">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            className="w-5 h-5 text-blue-600 dark:text-blue-400 rounded mt-0.5"
            defaultChecked
          />
          <div className="text-sm text-gray-700 dark:text-white/60">
            I agree to the <a href="#" className="text-blue-600 dark:text-blue-400 hover:underline">booking terms</a> and <a href="#" className="text-blue-600 dark:text-blue-400 hover:underline">cancellation policy</a>. 
            I understand that I can cancel free of charge up to 24 hours before the appointment.
          </div>
        </label>
      </div>

      {/* Security Notice */}
      <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800/30 rounded-lg p-4 flex items-start gap-3">
        <Shield className="w-5 h-5 text-purple-600 dark:text-purple-400 mt-0.5" />
        <div className="text-sm text-purple-800 dark:text-purple-300">
          <strong>Blockchain-Secured Booking:</strong> This appointment will be recorded on Solana blockchain. 
          All data sharing permissions and access logs are cryptographically secured and auditable.
        </div>
      </div>
    </div>
  );
}